import type { AxiosRequestConfig, AxiosResponse } from "axios";
import request, { type ApiResponse } from "./request";

type RequestParams = Record<string, unknown>;

function unwrap<T>(promise: Promise<AxiosResponse<T>>) {
  return promise.then((res) => res.data);
}

/** 从 Content-Disposition 解析文件名 */
function parseFilename(contentDisposition?: string, fallback = "download") {
  if (!contentDisposition) return fallback;
  const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) {
    try {
      return decodeURIComponent(utf8Match[1]);
    } catch {
      return utf8Match[1];
    }
  }
  const match = contentDisposition.match(/filename="?([^";]+)"?/i);
  return match?.[1] || fallback;
}

/** 触发浏览器下载 */
function saveBlob(blob: Blob, filename: string) {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.style.display = "none";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

/**
 * 统一 API 方法
 *
 * @example
 * await api.get('/orders', { page: 1 })
 * await api.post('/orders', { items: [] })
 * await api.put('/orders/1', { status: 'done' })
 * await api.delete('/orders/1')
 * await api.upload('/files', file, { type: 'avatar' })
 * await api.download('/export', { year: 2026 }, '报表.xlsx')
 */
export const api = {
  get<T = unknown>(
    url: string,
    params?: RequestParams,
    config?: AxiosRequestConfig,
  ) {
    return unwrap<T>(request.get(url, { ...config, params }));
  },

  post<T = unknown>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig,
  ) {
    return unwrap<T>(request.post(url, data, config));
  },

  put<T = unknown>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig,
  ) {
    return unwrap<T>(request.put(url, data, config));
  },

  delete<T = unknown>(
    url: string,
    params?: RequestParams,
    config?: AxiosRequestConfig,
  ) {
    return unwrap<T>(request.delete(url, { ...config, params }));
  },

  /**
   * 上传文件（multipart/form-data）
   * @param file 单个文件，或多个文件 / 额外字段组成的 FormData
   * @param extra 附加表单字段
   */
  upload<T = unknown>(
    url: string,
    file: File | Blob | FormData,
    extra?: RequestParams,
    config?: AxiosRequestConfig,
  ) {
    let formData: FormData;

    if (file instanceof FormData) {
      formData = file;
    } else {
      formData = new FormData();
      formData.append("file", file);
    }

    if (extra) {
      Object.entries(extra).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          formData.append(key, String(value));
        }
      });
    }

    return unwrap<T>(
      request.post(url, formData, {
        ...config,
        headers: {
          "Content-Type": "multipart/form-data",
          ...config?.headers,
        },
      }),
    );
  },

  /**
   * 下载文件
   * @param filename 指定保存文件名；不传则尝试从响应头解析
   * @param method 默认 post（兼容旧接口），可改为 get
   */
  async download(
    url: string,
    data?: RequestParams,
    filename?: string,
    config?: AxiosRequestConfig & { method?: "get" | "post" },
  ) {
    const method = config?.method ?? "post";
    const { method: _ignored, ...rest } = config ?? {};

    const response = await request.request<Blob>({
      url,
      method,
      responseType: "blob",
      ...(method === "get" ? { params: data } : { data }),
      ...rest,
    });

    const blob = response.data;
    const name = parseFilename(
      response.headers["content-disposition"],
      filename || "download",
    );
    saveBlob(blob, name);
    return blob;
  },
};

export const { get, post, put, delete: del, upload, download } = api;

export { default as request } from "./request";
export type { ApiResponse };
export default api;
